import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  ShieldCheck, ShieldAlert, ShieldX, Download, Loader2,
  Mail, Building2, Calendar, BadgeCheck,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  active:      { label: "VERIFIED",          tone: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: ShieldCheck, accent: "bg-emerald-500" },
  expired:     { label: "EXPIRED",           tone: "bg-amber-50 text-amber-800 border-amber-200",        Icon: ShieldAlert, accent: "bg-amber-500" },
  deactivated: { label: "DEACTIVATED",       tone: "bg-red-50 text-red-700 border-red-200",              Icon: ShieldX,     accent: "bg-red-500" },
  not_found:   { label: "NOT FOUND",         tone: "bg-red-50 text-red-700 border-red-200",              Icon: ShieldX,     accent: "bg-red-500" },
};

function downloadVCard(m) {
  const lines = [
    "BEGIN:VCARD", "VERSION:3.0",
    `FN:${m.name || ""}`,
    `TITLE:${m.title || ""}`,
    "ORG:Magnus Kor Talent Partners",
    `NOTE:Member ID ${m.member_id} (verified via magnuskor.com)`,
    `URL:https://magnuskor.com/verify/${m.member_id}`,
    "END:VCARD",
  ];
  const blob = new Blob([lines.join("\n")], { type: "text/vcard" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = `${m.member_id}.vcf`; a.click();
  URL.revokeObjectURL(url);
}

export default function VerifyPage() {
  const { memberId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/verify/${encodeURIComponent(memberId)}`)
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) setErr("Unable to verify right now. Please try again later."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [memberId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0f0f10] text-white flex flex-col items-center justify-center" data-testid="verify-loading">
        <Loader2 className="animate-spin text-[#B8860B]" size={32} />
        <p className="mt-4 text-sm text-gray-400">Verifying credential…</p>
      </div>
    );
  }

  if (err || !data) {
    return (
      <div className="min-h-screen bg-[#0f0f10] text-white flex flex-col items-center justify-center px-6" data-testid="verify-error">
        <ShieldX className="text-red-400" size={48} />
        <p className="mt-4 text-lg font-medium">Verification failed</p>
        <p className="text-sm text-gray-400 mt-1 text-center max-w-sm">{err || "No response."}</p>
      </div>
    );
  }

  const meta = STATUS_META[data.status] || STATUS_META.not_found;
  const StatusIcon = meta.Icon;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0f0f10] via-[#15151a] to-[#0f0f10] text-white" data-testid={`verify-page-${data.status}`}>
      <div className="max-w-md mx-auto px-5 pt-10 pb-16">
        {/* Brand bar */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="text-[#B8860B] font-serif text-xl tracking-wide">MAGNUS KOR</div>
            <div className="text-[10px] text-gray-500 tracking-[0.2em] uppercase">Talent Partners</div>
          </div>
          <a href="https://magnuskor.com" className="text-xs text-gray-400 hover:text-[#B8860B]" data-testid="verify-home-link">
            magnuskor.com →
          </a>
        </div>

        {/* Status banner */}
        <div className={`relative overflow-hidden rounded-xl border ${meta.tone} px-5 py-6 mb-6`} data-testid="verify-status-banner">
          <div className={`absolute left-0 top-0 bottom-0 w-1 ${meta.accent}`} />
          <div className="flex items-center gap-3">
            <StatusIcon size={28} />
            <div>
              <div className="text-xs uppercase tracking-[0.18em] opacity-70">Identity Status</div>
              <div className="text-xl font-semibold leading-tight">{meta.label}</div>
            </div>
          </div>
          <p className="text-sm mt-3 opacity-90">{data.message}</p>
        </div>

        {/* Identity card */}
        {data.name && (
          <div className="bg-[#1a1a1f] border border-white/10 rounded-xl overflow-hidden mb-6">
            <div className="p-6 flex items-start gap-5">
              {data.photo_base64 ? (
                <img
                  src={data.photo_base64}
                  alt={data.name}
                  className="w-24 h-32 object-cover border-2 border-[#B8860B] rounded"
                  data-testid="verify-photo"
                />
              ) : (
                <div className="w-24 h-32 flex items-center justify-center bg-[#262630] border-2 border-[#B8860B] rounded text-[#B8860B] font-serif text-3xl" data-testid="verify-photo-placeholder">
                  {(data.name || "MK").split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.18em] text-gray-500">Holder</div>
                <h1 className="text-xl font-semibold leading-tight" data-testid="verify-name">{data.name}</h1>
                <div className="mt-1 inline-flex items-center gap-1.5 px-2 py-0.5 text-[11px] uppercase tracking-wider bg-[#B8860B]/15 text-[#D4A12C] border border-[#B8860B]/30 rounded-full">
                  <BadgeCheck size={11} /> {data.title}
                </div>
                {data.specialist && (
                  <div className="text-xs text-gray-400 mt-2">
                    <span className="opacity-60">Specialist:</span> {data.specialist}
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 border-t border-white/10 text-xs">
              <div className="p-4 border-r border-white/10">
                <div className="text-[10px] uppercase tracking-[0.15em] text-gray-500">Member ID</div>
                <div className="font-mono mt-1 text-gray-100" data-testid="verify-member-id">{data.member_id}</div>
              </div>
              <div className="p-4">
                <div className="text-[10px] uppercase tracking-[0.15em] text-gray-500">Validity</div>
                <div className="mt-1 text-gray-100">
                  {data.permanent ? "Permanent" : (data.expiry_date || "—")}
                </div>
              </div>
              {data.join_date && (
                <div className="p-4 border-t border-white/10 col-span-2 flex items-center gap-2 text-gray-400">
                  <Calendar size={12} /> Issued on {data.join_date}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Actions */}
        {data.verified && data.name && (
          <button
            onClick={() => downloadVCard(data)}
            className="w-full flex items-center justify-center gap-2 bg-[#B8860B] hover:bg-[#9A7209] text-white text-sm font-medium py-3 rounded-lg transition mb-3"
            data-testid="verify-save-contact"
          >
            <Download size={14} /> Save contact to phone
          </button>
        )}

        {/* Footer issuer block */}
        <div className="mt-8 text-center text-[11px] text-gray-500 leading-relaxed">
          <div className="flex items-center justify-center gap-1.5">
            <Building2 size={11} /> L&D Nexus Limited · Company No. 16450617
          </div>
          <div className="mt-1">71-75 Shelton Street, London WC2H 9JQ</div>
          <a href="mailto:office@magnuskor.com" className="inline-flex items-center gap-1 mt-2 text-gray-400 hover:text-[#B8860B]" data-testid="verify-contact-link">
            <Mail size={11} /> Report a lost or stolen card
          </a>
        </div>
      </div>
    </div>
  );
}
